import { Col } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { Button } from 'common/components/Button/Button'
import Show from 'common/components/Show2/Show'
import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { useTimezone } from 'common/hooks/useTimezone'
import { DateService } from 'common/services/dateService'
import { DocumentResourceHeader } from 'features/Home/Book/Booking/Schedule/PatientSchedule/DocumentResourceHeader/DocumentResourceHeader'
import { EmptyRoomsList } from 'features/Home/Book/Booking/Schedule/PatientSchedule/EmptyRoomsList/EmptyRoomsList'
import { PatientScheduleSkeleton } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientScheduleSkeleton/PatientScheduleSkeleton'
import { PatientSelection } from 'features/Home/Book/Booking/Schedule/PatientSchedule/PatientSelection/PatientSelection'
import { ScheduleCalendar } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/ScheduleCalendar'
import { ScheduleDoctorRail } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/ScheduleDoctorRail'
import {
  ALL_STAFF_INSTANCE_ID,
  applyScheduleDoctorRailOrder,
  buildScheduleInstanceKey,
  filterWaitingRoomSchedulesForStaffFocus,
  getScheduleDoctorRailStaff,
  getVisibleScheduleStaff,
  getScheduleStaffColorByIndex,
  isAllConsultationStaffSelected,
  isNoConsultationStaffSelected,
  type ScheduleStaffRole,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'
import { orderScheduleRooms } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleRoomsOrder'
import { StaffResourceHeader } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/StaffResourceHeader'
import { useScheduleDoctorRailOrder } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useScheduleDoctorRailOrder'
import { useScheduleHeight } from 'features/Home/Book/Booking/Schedule/PatientSchedule/useScheduleHeight'
import {
  ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS,
  INFO_CONSTANTS,
  PATIENT_STATUSES,
} from 'features/Home/constants/infoConstants'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'
import { ManageAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageAppointment'
import { ManageEhrAppointment } from 'features/Schedule/pages/components/ManageAppointment/ManageEhrAppointment'
import { useScheduleActions } from 'features/Schedule/pages/components/ScheduleDayView/hooks/useScheduleActions'
import { useScheduleData } from 'features/Schedule/pages/components/ScheduleDayView/hooks/useScheduleData'
import { isScheduleDaySyncedToDate } from 'features/Schedule/pages/components/utils'
import { type EScheduleView } from 'features/Schedule/pages/Schedule'
import { useSchedulePusher } from 'features/Schedule/pusher/useSchedulePusher'
import { PathUtils } from 'routes/routes.utils'
import pageStyles from '../../schedule.module.scss'

const formatWaitingRoomDoctorName = (name: string): string => {
  const trimmed = name.replace(/^Dr\.?\s*/i, '').trim()
  const [last, first] = trimmed.split(',').map((part) => part.trim())
  const parts = (first ? `${first} ${last}` : trimmed).split(/\s+/).filter(Boolean)
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : trimmed
}

const { getCurrentTimeInTimezone, preserveDateInTimezone } = DateService

interface ScheduleDayViewProps {
  defaultActiveDate: string | null
  selectedConsultationStaffIds: string[] | null
  selectedRoomIds?: string[] | null
  showCanceled: boolean
  scheduleView: EScheduleView
  scheduleMonthData?: ScheduleMonth
  zoomInterval: number
  staffRoleUsers?: ScheduleStaffRole[]
}

export const ScheduleDayView = ({
  defaultActiveDate,
  selectedConsultationStaffIds,
  selectedRoomIds,
  showCanceled,
  scheduleView,
  scheduleMonthData,
  zoomInterval,
  staffRoleUsers = [],
}: ScheduleDayViewProps) => {
  const { timeZone } = useTimezone()

  const navigate = useNavigate()
  const { siteId } = useParams()

  const [editedPatient, setEditedPatient] = useState<IPatientSchedule | null>(null)
  const [addPatient, setAddPatient] = useState<boolean>(false)
  const [customAppointmentData, setCustomAppointmentData] = useState<{
    appointment: Date[]
    room: string
    consultationStaffId?: number
  } | null>(null)

  const today = useMemo(() => {
    return getCurrentTimeInTimezone(timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [timeZone])

  const {
    scheduleDay,
    isFetchingDay,
    isLoadingDay,
    rooms,
    schedules,
    consultationStaffs,
    languages,
    timeInterval,
    disabledIntervals,
    eventsToSendNotification,
    doctorBufferTime,
    roomStatuses,
    defaultAppointmentTime,
    patientStatuses,
    appointmentCategories,
    hasEhrBooking,
    byPassTemperatureCheck,
    hasOccupied,
    appointmentColor,
    punctuality,
    patientBufferTime,
    availability,
  } = useScheduleData(defaultActiveDate, selectedConsultationStaffIds, showCanceled)

  const visibleRooms = useMemo(
    () => orderScheduleRooms(rooms, selectedRoomIds),
    [rooms, selectedRoomIds],
  )

  const visibleDisabledIntervals = useMemo(() => {
    if (visibleRooms.length === rooms.length) return disabledIntervals
    const visibleResourceIds = new Set(visibleRooms.map((room) => `${room.label}/${room.value}`))
    return disabledIntervals.filter((interval) => visibleResourceIds.has(interval.resourceId))
  }, [disabledIntervals, rooms.length, visibleRooms])

  useSchedulePusher(defaultActiveDate)

  useEffect(() => {
    if (!editedPatient?.id) return

    const latestPatient = schedules.find((schedule) => schedule.id === editedPatient.id)
    if (!latestPatient || latestPatient === editedPatient) return

    const hasEncounterChanged =
      latestPatient.emr_encounter_id !== editedPatient.emr_encounter_id ||
      latestPatient.emr_encounter_uuid !== editedPatient.emr_encounter_uuid ||
      latestPatient.emr_updated_at !== editedPatient.emr_updated_at

    if (hasEncounterChanged) {
      setEditedPatient(latestPatient)
    }
  }, [schedules, editedPatient])

  const {
    updateAppointment,
    updateRoomStatus,
    notifyMultiplePatients,
    isNotifying,
    isUpdatingRoomStatus,
    isNotifyingMultiple,
  } = useScheduleActions()

  const isToday = useMemo(() => {
    if (!defaultActiveDate || !timeZone) return false
    return preserveDateInTimezone(defaultActiveDate, timeZone).isSame(
      preserveDateInTimezone(today, timeZone),
      'day',
    )
  }, [defaultActiveDate, timeZone, today])

  const isFutureDay = useMemo(() => {
    if (!defaultActiveDate || !timeZone) return false
    return preserveDateInTimezone(defaultActiveDate, timeZone).isAfter(
      preserveDateInTimezone(today, timeZone),
      'day',
    )
  }, [defaultActiveDate, timeZone, today])

  const visibleStaff = useMemo(
    () =>
      getVisibleScheduleStaff(
        consultationStaffs,
        schedules,
        selectedConsultationStaffIds,
        staffRoleUsers,
      ),
    [consultationStaffs, schedules, selectedConsultationStaffIds, staffRoleUsers],
  )

  const { doctorRailOrder, saveDoctorRailOrder } = useScheduleDoctorRailOrder()

  const railStaff = useMemo(
    () => applyScheduleDoctorRailOrder(getScheduleDoctorRailStaff(visibleStaff), doctorRailOrder),
    [visibleStaff, doctorRailOrder],
  )

  const [focusedStaffIds, setFocusedStaffIds] = useState<number[]>([])

  useEffect(() => {
    setFocusedStaffIds([])
  }, [defaultActiveDate])

  useEffect(() => {
    const railIds = new Set(railStaff.map((staff) => staff.id))
    setFocusedStaffIds((current) => {
      const next = current.filter((staffId) => railIds.has(staffId))
      return next.length === current.length ? current : next
    })
  }, [railStaff])

  const waitingRoomStaff = useMemo(
    () =>
      focusedStaffIds.length
        ? railStaff.filter((staff) => focusedStaffIds.includes(staff.id))
        : railStaff,
    [focusedStaffIds, railStaff],
  )

  const displayedSchedules = useMemo(
    () => filterWaitingRoomSchedulesForStaffFocus(schedules, focusedStaffIds, visibleRooms),
    [focusedStaffIds, schedules, visibleRooms],
  )

  const sharedInstanceKey = buildScheduleInstanceKey({
    view: isToday ? 'today' : 'day',
    date: defaultActiveDate ?? '',
    staffId: ALL_STAFF_INSTANCE_ID,
  })

  const staffColumnInstanceKey = buildScheduleInstanceKey({
    view: 'day',
    date: defaultActiveDate ?? '',
    staffId: ALL_STAFF_INSTANCE_ID,
  })

  const defaultStaffColumnRoomId = useMemo(() => {
    const waitingRoom = visibleRooms.find((room) => room.is_waiting_room)
    const room = waitingRoom ?? visibleRooms[0]
    if (!room) return ''
    return `${room.label}/${room.value}`
  }, [visibleRooms])

  const stackHeight = useScheduleHeight()

  const isDayDataSynced = isScheduleDaySyncedToDate(defaultActiveDate, scheduleDay)

  const isLoading = !defaultActiveDate || isLoadingDay || (isFetchingDay && !isDayDataSynced)
  const hasNoBooking = !isLoading && (!timeInterval?.max || !timeInterval?.min || !rooms?.length)

  const getWaitingRoomPatients = (staffId?: number) => {
    try {
      const waitingRoom = schedules.find((room: IPatientSchedule) => {
        return room.room.is_waiting_room
      })
      if (waitingRoom) {
        const waitingRoomCode = waitingRoom.room.value
        const checkedPatientsInWaitingRoom = schedules.filter((patient: IPatientSchedule) => {
          return (
            patient.room.value === waitingRoomCode &&
            (staffId == null || Number(patient.consultation_staff || 0) === staffId) &&
            patient.status.code === PATIENT_STATUSES.CHECKED_IN_ON_SITE
          )
        })
        return checkedPatientsInWaitingRoom.length
      }
      return null
    } catch (error) {
      return null
    }
  }

  const updateStatus = (value: number, room: IPatientRoom) => {
    updateRoomStatus(
      room,
      {
        status_type_code: value,
        lease_id: room?.booking_id,
      },
      defaultActiveDate,
    )
  }

  const handleCloseModal = () => {
    setAddPatient(false)
    setEditedPatient(null)
    setCustomAppointmentData(null)
  }

  const createScheduleWithCustomAppointment = (
    start: Date,
    end: Date,
    resourceId: string,
    consultationStaffId?: number,
  ): void => {
    setCustomAppointmentData({ appointment: [start, end], room: resourceId, consultationStaffId })
    setAddPatient(true)
  }

  const roomsHeader = useMemo(() => {
    if (!defaultActiveDate || !timeZone) return undefined
    const isSameDate = isToday

    return visibleRooms.flatMap((room: IPatientRoom) => {
      const members = room.is_waiting_room && waitingRoomStaff.length ? waitingRoomStaff : [null]
      return members.map((staff) => ({
        resourceId: staff
          ? `${room.label}/${room.value}/staff/${staff.id}`
          : `${room.label}/${room.value}`,
        roomResourceId: `${room.label}/${room.value}`,
        staffId: staff?.id,

        resourceTemplate: (
          <div>
            {staff && (
              <span
                aria-hidden='true'
                className='schedule-waiting-room-header-border'
                style={{
                  backgroundColor: getScheduleStaffColorByIndex(
                    railStaff.findIndex((member) => member.id === staff.id),
                  ),
                }}
              />
            )}
            <DocumentResourceHeader
              hasOccupied={hasOccupied}
              doctorsBufferTime={doctorBufferTime}
              room={room}
              hasRoomStatus={!room.is_waiting_room && !!room.add_patient && isSameDate}
              statusList={roomStatuses}
              updateStatus={updateStatus}
              timeZone={timeZone}
              key={room.id}
              disabledIntervals={visibleDisabledIntervals}
              dayInterval={timeInterval}
              isUpdatingRoomStatus={isUpdatingRoomStatus}
              {...(room.is_waiting_room && {
                patientsCount: getWaitingRoomPatients(staff?.id),
                staffFilterTag: staff ? formatWaitingRoomDoctorName(staff.name) : '',
              })}
            />
          </div>
        ),
      }))
    })
  }, [
    JSON.stringify(visibleRooms),
    JSON.stringify(visibleDisabledIntervals),
    JSON.stringify(roomStatuses),
    JSON.stringify(doctorBufferTime),
    JSON.stringify(timeInterval),
    timeZone,
    isUpdatingRoomStatus,
    defaultActiveDate,
    isToday,
    waitingRoomStaff,
    railStaff,
    schedules,
  ])

  const staffHeader = useMemo(
    () =>
      visibleStaff.map((staff) => ({
        resourceId: String(staff.id),
        resourceTemplate: <StaffResourceHeader name={staff.name} />,
      })),
    [visibleStaff],
  )

  const disabledInputsManageAppointment = useMemo(() => {
    if (!editedPatient) return false
    return !ALLOW_RESCHEDULE_CURRENT_DAY_PAST_EVENTS.includes(editedPatient?.status.code)
  }, [editedPatient])

  const handleUpdateEvents = useCallback(
    async (adjustedData: any, event: any) => {
      return await updateAppointment(adjustedData, defaultActiveDate, event.id)
    },
    [defaultActiveDate],
  )

  const manageAppointmentTitle = useMemo(() => {
    if (editedPatient) {
      return INFO_CONSTANTS.EDIT_APPOINTMENT
    }
    return INFO_CONSTANTS.ADD_APPOINTMENT
  }, [editedPatient, defaultActiveDate])

  const handleToggleFocusedStaff = useCallback((staffId: number) => {
    setFocusedStaffIds((current) =>
      current.includes(staffId) ? current.filter((id) => id !== staffId) : [...current, staffId],
    )
  }, [])

  const handleClearFocusedStaff = useCallback(() => {
    setFocusedStaffIds([])
  }, [])

  const prefillConsultationStaffId =
    focusedStaffIds.length === 1
      ? focusedStaffIds[0]
      : visibleStaff.length === 1
      ? visibleStaff[0].id
      : undefined

  const availabilityStaffIds = useMemo(
    () => waitingRoomStaff.filter((staff) => staff.id !== 0).map((staff) => staff.id),
    [waitingRoomStaff],
  )

  return (
    <Col xs={24} className={pageStyles.scheduleBody}>
      <Show>
        <Show.When isVisible={isLoading}>
          <PatientScheduleSkeleton />
        </Show.When>
        <Show.When isVisible={hasNoBooking}>
          <EmptyRoomsList
            title={INFO_CONSTANTS.NO_ACTIVE_SCHEDULE_TODAY}
            content={
              <Button onClick={() => navigate(PathUtils.getBook(siteId))}>Book now →</Button>
            }
          />
        </Show.When>
        <Show.When isVisible={isNoConsultationStaffSelected(selectedConsultationStaffIds)}>
          <EmptyRoomsList title={INFO_CONSTANTS.NO_CONSULTATION_STAFF_SELECTED} />
        </Show.When>
        <Show.Else>
          {visibleStaff.length > 0 && isFutureDay ? (
            <ScheduleCalendar
              key={staffColumnInstanceKey}
              appointmentColor={appointmentColor}
              sendInfoPatientsNotification={notifyMultiplePatients}
              setEditedPatient={setEditedPatient}
              createCustomAppointment={(start, end, staffResourceId) =>
                createScheduleWithCustomAppointment(
                  start,
                  end,
                  defaultStaffColumnRoomId,
                  Number(staffResourceId),
                )
              }
              handleUpdateEvents={handleUpdateEvents}
              showConsultationStaffInitials={false}
              resources={staffHeader}
              prepopulateTimeOnClick={defaultAppointmentTime}
              dayInterval={timeInterval}
              interval={zoomInterval}
              eventsToSendNotification={eventsToSendNotification}
              isSendingNotifications={isNotifying || isNotifyingMultiple}
              disabledIntervals={visibleDisabledIntervals}
              timeZone={timeZone}
              rooms={visibleRooms}
              schedules={schedules}
              patientStatuses={patientStatuses}
              appointmentCategories={appointmentCategories}
              hasEhrBooking={hasEhrBooking}
              instanceKey={staffColumnInstanceKey}
              resourceGrouping='staff'
              showAnnounceButton={false}
              showNotifyPatients
              availability={availability}
            />
          ) : visibleStaff.length > 0 ? (
            <div className={pageStyles.doctorDayLayout} style={{ height: stackHeight }}>
              {railStaff.length > 0 ? (
                <ScheduleDoctorRail
                  staff={railStaff}
                  focusedStaffIds={focusedStaffIds}
                  onToggleStaff={handleToggleFocusedStaff}
                  onClear={handleClearFocusedStaff}
                  onReorder={saveDoctorRailOrder}
                />
              ) : null}
              <div className={pageStyles.doctorDayCalendar}>
                <ScheduleCalendar
                  key={sharedInstanceKey}
                  appointmentColor={appointmentColor}
                  sendInfoPatientsNotification={notifyMultiplePatients}
                  setEditedPatient={setEditedPatient}
                  createCustomAppointment={(start, end, roomId) =>
                    createScheduleWithCustomAppointment(
                      start,
                      end,
                      roomsHeader?.find((resource) => resource.resourceId === roomId)
                        ?.roomResourceId ?? roomId,
                      roomsHeader?.find((resource) => resource.resourceId === roomId)?.staffId ??
                        prefillConsultationStaffId,
                    )
                  }
                  handleUpdateEvents={handleUpdateEvents}
                  showConsultationStaffInitials
                  resources={roomsHeader ?? []}
                  prepopulateTimeOnClick={defaultAppointmentTime}
                  dayInterval={timeInterval}
                  interval={zoomInterval}
                  eventsToSendNotification={eventsToSendNotification}
                  isSendingNotifications={isNotifying || isNotifyingMultiple}
                  disabledIntervals={visibleDisabledIntervals}
                  timeZone={timeZone}
                  rooms={visibleRooms}
                  schedules={displayedSchedules}
                  patientStatuses={patientStatuses}
                  appointmentCategories={appointmentCategories}
                  hasEhrBooking={hasEhrBooking}
                  instanceKey={sharedInstanceKey}
                  focusedStaffIds={focusedStaffIds}
                  scaleColumnsByOverlap={false}
                  showAnnounceButton={isToday}
                  showNotifyPatients
                  availability={availability}
                  availabilityStaffIds={availabilityStaffIds}
                />
              </div>
            </div>
          ) : isAllConsultationStaffSelected(selectedConsultationStaffIds) ? (
            <ScheduleCalendar
              key={`${siteId ?? 'site'}-${defaultActiveDate ?? 'day'}`}
              appointmentColor={appointmentColor}
              sendInfoPatientsNotification={notifyMultiplePatients}
              setEditedPatient={setEditedPatient}
              createCustomAppointment={createScheduleWithCustomAppointment}
              handleUpdateEvents={handleUpdateEvents}
              showConsultationStaffInitials={!selectedConsultationStaffIds?.length}
              resources={roomsHeader ?? []}
              prepopulateTimeOnClick={defaultAppointmentTime}
              dayInterval={timeInterval}
              interval={zoomInterval}
              eventsToSendNotification={eventsToSendNotification}
              isSendingNotifications={isNotifying || isNotifyingMultiple}
              disabledIntervals={visibleDisabledIntervals}
              timeZone={timeZone}
              rooms={visibleRooms}
              schedules={schedules}
              patientStatuses={patientStatuses}
              appointmentCategories={appointmentCategories}
              hasEhrBooking={hasEhrBooking}
              instanceKey={buildScheduleInstanceKey({
                view: isToday ? 'today' : 'day',
                date: defaultActiveDate ?? '',
                staffId: selectedConsultationStaffIds?.join(',') || 'all',
              })}
              availability={availability}
            />
          ) : null}
        </Show.Else>
      </Show>

      <Show>
        <Show.When isVisible={hasEhrBooking}>
          <ManageEhrAppointment
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
            punctuality={punctuality}
            patientBufferTime={patientBufferTime}
            scheduleMonthData={scheduleMonthData}
          />
        </Show.When>
        <Show.Else>
          <ManageAppointment
            scheduleView={scheduleView}
            patientStatuses={patientStatuses}
            title={manageAppointmentTitle}
            date={defaultActiveDate}
            open={addPatient || !!editedPatient}
            isEditing={!!editedPatient}
            editedPatientData={editedPatient}
            setOpen={handleCloseModal}
            consultation_staffs={consultationStaffs}
            dayInterval={timeInterval}
            languages={languages}
            prePopulateSchedule={defaultAppointmentTime}
            disabledEverything={disabledInputsManageAppointment}
            customAppointment={customAppointmentData}
            rooms={rooms}
            schedules={schedules}
            timeInterval={timeInterval}
            disabledIntervals={disabledIntervals}
            timeZone={timeZone}
            appointmentCategories={appointmentCategories}
            isSendingNotifications={false}
            byPassTemperatureCheck={byPassTemperatureCheck}
          />
        </Show.Else>
      </Show>

      <PatientSelection />
    </Col>
  )
}
